import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';

import { useTranslator } from '../../../../../../components/i18n/useTranslator';
import { getElementId, getTitle } from '../../../../../../../common/elements/utils/elementPropertyUtils';
import { addRootToTag, updateTag } from '../../../../../../../common/tags/tagActions';
import Icon from '../../../../../../components/icons/Icon';

import { Button } from '@milanote/roma';
import { ArrowNarrowRight } from '@untitledui/icons';
import { TagEditContentProps } from '../tagComponentPropTypes';
import { getHighestTagBoardId, isTagRootUsed } from '../tagUtils';
import { getUniqueArray } from '../../../../../../../common/utils/arrayUtils';

const TagMoveButtons: React.FC<TagEditContentProps> = ({
    tag,
    taggedElements,
    userRootPath,
    shareRootPath,
    closePopups,
    boardVirtualPathToRootMap,
    contextRootsInCurrentPath,
}) => {
    if (!tag || !userRootPath) return null;

    const dispatch = useDispatch();

    const translate = useTranslator('popup', 'tags.edit');
    const highestRootId = getHighestTagBoardId(tag, userRootPath);
    const shareRootIds = shareRootPath.map(getElementId);
    const userRootPathIds = userRootPath.map(getElementId);

    // Find the first board in the path where a tag is used either on that board or a descendant of that board that is outside the current path
    const firstDisabledBoardIndex = useMemo(() => {
        if (!boardVirtualPathToRootMap || isEmpty(taggedElements)) return -1;

        // Get a path to root for each board with a tag usage on it, and filter to only those that are within the current hierarchy of the tag
        const elementPaths = taggedElements
            .map((el) => (el.elementParentId ? boardVirtualPathToRootMap[el.elementParentId] : null))
            .filter((path) => path && path.includes(highestRootId));

        // Find the first board where not all tagged elements share that board as an ancestor
        return userRootPathIds.findIndex((boardId) => !elementPaths?.every((path) => path?.includes(boardId)));
    }, [taggedElements, userRootPath, boardVirtualPathToRootMap]);

    const handleMoveTag = (newRootBoardId: string) => {
        if (!boardVirtualPathToRootMap || !highestRootId) return;

        const isHighestInCurrentContext = shareRootIds.includes(highestRootId);
        const isNewInCurrentContext = shareRootIds.includes(newRootBoardId);

        // If a tag is used within the current context and the user is moving it up out of the current context,
        // then we might need to keep the original rootBoardId if it's used on an element
        if (isHighestInCurrentContext && !isNewInCurrentContext) {
            if (isTagRootUsed(taggedElements, contextRootsInCurrentPath, highestRootId, boardVirtualPathToRootMap)) {
                dispatch(addRootToTag(tag, newRootBoardId));
                closePopups();
                return;
            }
        }

        const newIndex = userRootPathIds.indexOf(newRootBoardId);
        if (newIndex === -1) return;

        const excludedBoardsPath = new Set(userRootPathIds.slice(0, newIndex));

        const rootIdsToRemove = tag.rootBoardIds.filter((rootBoardId) => {
            const path = boardVirtualPathToRootMap[rootBoardId];

            // If the current tag root sits outside the current tag hierarchy, treat it as totally separate and don't remove it
            if (!path || !path?.includes(highestRootId)) return false;

            const lowestSharedBoard = path.find((boardId) => excludedBoardsPath.has(boardId));
            return Boolean(lowestSharedBoard);
        });

        const newRootBoardIds = tag.rootBoardIds.filter((id) => !rootIdsToRemove.includes(id));
        newRootBoardIds.push(newRootBoardId);

        dispatch(updateTag(tag.id, { rootBoardIds: getUniqueArray(newRootBoardIds) }));
        closePopups();
    };

    return (
        <ul className="TagsMoveButtons mt-2">
            {userRootPath.map((element, index) => {
                const id = getElementId(element);
                const title = getTitle(element);
                const isTagOnBoard = id === highestRootId;
                const isDisabled = firstDisabledBoardIndex > -1 && index >= firstDisabledBoardIndex;

                return (
                    <li key={id} className={`flex flex-row`} style={{ marginLeft: `${index * 2}rem` }}>
                        <Button
                            className="flex flex-row items-center"
                            iconLeading={!isTagOnBoard && ArrowNarrowRight}
                            color={'link-gray'}
                            onClick={() => handleMoveTag(id)}
                            isDisabled={isDisabled}
                        >
                            <div className="flex w-full gap-1">
                                {isTagOnBoard && <Icon name="green-tick" />}
                                <span className={`text-left text-sm ml-1`}>
                                    {isTagOnBoard ? translate('in') : translate('moveTo')}
                                    {'\u00A0'}
                                    <span className="font-bold">{title}</span>
                                </span>
                            </div>
                        </Button>
                    </li>
                );
            })}
        </ul>
    );
};

export default TagMoveButtons;
